import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import type { ProfileSetupMissingItem } from './profileSetupBanner';

type ProfileSetupIncompleteCardProps = {
    missingItems: ProfileSetupMissingItem[];
    onAddItem: (item: ProfileSetupMissingItem) => void;
};

export default function ProfileSetupIncompleteCard({
    missingItems,
    onAddItem,
}: ProfileSetupIncompleteCardProps) {
    const theme = useTheme();

    if (missingItems.length === 0) return null;

    const borderColor = theme.palette.red.main;
    const softBg = alpha(theme.palette.red.main, 0.06);

    return (
        <Box
            role='status'
            sx={{
                width: '100%',
                borderRadius: 2,
                border: `1px solid ${borderColor}`,
                bgcolor: softBg,
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2.25 },
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 3 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent='space-between'
            >
                <Stack
                    direction='row'
                    spacing={1.75}
                    alignItems='flex-start'
                    sx={{ minWidth: 0, flex: 1 }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: theme.palette.red.main,
                            color: theme.palette.common.white,
                        }}
                    >
                        <AssignmentOutlinedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant='subtitle1'
                            sx={{
                                fontWeight: 700,
                                color: 'primary.dark',
                                lineHeight: 1.3,
                            }}
                        >
                            Complete your profile
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{ color: 'text.secondary', mt: 0.5 }}
                        >
                            Please add the following information to finish
                            setting up your account.
                        </Typography>
                    </Box>
                </Stack>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 3 }}
                    sx={{
                        flexShrink: 0,
                        width: { xs: '100%', sm: 'auto' },
                        pl: { xs: 7, sm: 0 },
                    }}
                >
                    {missingItems.map((item) => (
                        <Stack
                            key={item.id}
                            direction='row'
                            spacing={0.75}
                            alignItems='center'
                            sx={{ flexWrap: 'nowrap' }}
                        >
                            <FiberManualRecordIcon
                                sx={{
                                    fontSize: 10,
                                    color: theme.palette.red.main,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant='body2'
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.label}
                            </Typography>
                            <ButtonBase
                                onClick={() => onAddItem(item)}
                                sx={{
                                    color: 'primary.main',
                                    borderRadius: 0.5,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: 'primary.main',
                                        fontWeight: 600,
                                    }}
                                >
                                    Add now &gt;
                                </Typography>
                            </ButtonBase>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
}
